import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  fetchWorkers,
  updateWorker,
  selectWorkersLoading,
  selectAllWorkers,
  selectWorkersStatus
} from 'store/slices/workersSlice';
import {
  WORKER_TYPES,
  WORKER_TYPES_LABELS,
  WORKER_PARSER_VALIDATION_TYPES,
  WORKER_PARSER_VALIDATION_TYPES_LABELS,
  WORKER_TYPE_ICONS,
  WORKER_PARSER_METHODS,
  WORKER_ACTION_TYPES,
  WORKER_ACTION_TYPES_LABELS,
  WORKER_PATH_OPERATIONS,
  WORKER_PATH_OPERATIONS_LABELS
} from 'constants/workers';
import Page from 'components/layout/Page/Page';
import WorkerTabs from './components/WorkerTabs/WorkerTabs';
import { Skeleton } from 'components/layout/loading';
import { CardHeader } from 'components/cards';
import { Badge } from 'components/badges';
import { Accordion } from 'components/layout/Accordion';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { Button, PrimaryButton } from 'components/buttons';
import FullScreenModal from 'components/layout/modals/FullScreenModal/FullScreenModal';
import BaseTextInput from 'components/forms/BaseTextInput/BaseTextInput';
import { ParserUrlForm } from './components/AddWorkerWizard/steps/ParserUrlStep/ParserUrlStep';
import { ParserValidationForm } from './components/AddWorkerWizard/steps/ParserValidationStep/ParserValidationStep';
import { ValidationLogicForm } from './components/AddWorkerWizard/steps/ValidationLogicStep/ValidationLogicStep';

const InfoBlock = ({ label, value, className = '' }) => (
  <div className={`flex items-center space-x-4 bg-gray-800/50 p-3 rounded border border-gray-700 ${className}`}>
    <span className="font-mono font-semibold text-xs bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded whitespace-nowrap">
      {label}
    </span>
    <span className="text-sm text-gray-300 break-all font-mono">
      {value}
    </span>
  </div>
);

const CodeBlock = ({ label, data, className = '' }) => {
  const content = useMemo(() => {
    if (!data) return null;
    try {
      return typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    } catch (_e) {
      return String(data);
    }
  }, [data]);

  return (
    <Accordion title={label} className={className}>
      <pre className="text-xs text-gray-300 bg-gray-900 p-3 rounded border border-gray-700 font-mono overflow-auto max-h-96 custom-scrollbar">
        {content || <span className="text-gray-500 italic">No data</span>}
      </pre>
    </Accordion>
  );
};

export default function ViewWorker() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const returnTo = location.state?.from || '/workers';

  const loading = useSelector(selectWorkersLoading);
  const workers = useSelector(selectAllWorkers);
  const status = useSelector(selectWorkersStatus);
  const [editModal, setEditModal] = useState(null);
  const [editName, setEditName] = useState('');
  const [updating, setUpdating] = useState(false);

  const worker = useMemo(() =>
    workers.find(w => (w._id === id || w.id === id)),
    [workers, id]
  );

  useEffect(() => {
    if (!worker && status === 'idle') {
      dispatch(fetchWorkers());
    }
  }, [worker, status, dispatch]);

  const isParser = worker?.type === WORKER_TYPES.PARSER;
  const isFetching = !worker && loading;
  const Icon = worker ? WORKER_TYPE_ICONS[worker.type] : null;

  const handleUpdate = async (configUpdate) => {
    setUpdating(true);
    try {
      const payload = {
        config: {
          ...worker.config,
          ...configUpdate
        }
      };
      delete payload.config.testResult;
      await dispatch(updateWorker({ id: worker._id, data: payload })).unwrap();
      setEditModal(null);
    } catch (_e) {
      // error handled by redux
    } finally {
      setUpdating(false);
    }
  };

  const handleNameSave = async () => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    setUpdating(true);
    try {
      await dispatch(updateWorker({ id: worker._id, data: { name: trimmed } })).unwrap();
      setEditModal(null);
    } catch (_e) {
      // error handled by redux
    } finally {
      setUpdating(false);
    }
  };

  const openNameModal = () => {
    setEditName(worker.name);
    setEditModal('name');
  };

  const handleConfigurationSave = (data) => {
    handleUpdate({
      url: data.url,
      method: data.method,
      headers: data.headers,
      body: data.body || null
    });
  };

  const handleValidationSave = (data) => {
    const update = {};
    if (data.validationType === WORKER_PARSER_VALIDATION_TYPES.REGEX) {
      update.pattern = data.expression;
      update.jsonPath = null;
    } else {
      update.jsonPath = data.expression;
      update.pattern = null;
    }
    update.operation = data.operation || WORKER_PATH_OPERATIONS.NONE;
    update.controlValue = data.controlValue ?? null;
    handleUpdate(update);
  };

  const handleActionsSave = (data) => {
    handleUpdate({
      onMatch: {
        action: data.onSuccess,
        messageTemplate: data.onSuccessMessage
      },
      onNoMatch: {
        action: data.onFailure,
        messageTemplate: data.onFailureMessage
      }
    });
  };

  const SectionEditButton = ({ onClick }) => (
    <button
      onClick={onClick}
      className="text-gray-400 hover:text-white transition-colors p-1 rounded hover:bg-white/10 cursor-pointer"
      title="Edit"
    >
      <PencilSquareIcon className="h-5 w-5" />
    </button>
  );

  const renderActionSection = (title, config) => {
    if (!config) return null;
    return (
      <div className="space-y-4">
        <InfoBlock
          label={title}
          value={WORKER_ACTION_TYPES_LABELS[config.action] || config.action}
        />
        {config.action === WORKER_ACTION_TYPES.UPDATE_MESSAGE && config.messageTemplate && (
          <CodeBlock
            label="Message Template"
            data={config.messageTemplate}
          />
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (isFetching) {
      return (
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      );
    }

    if (!worker) {
      return (
        <div className="max-w-7xl mx-auto text-center py-12 text-gray-500">
          Worker not found.
        </div>
      );
    }

    const headerContent = (
      <div className="flex items-center space-x-4">
        <div className="p-2 bg-gray-800 rounded-lg border border-gray-700">
          {Icon && <Icon className="h-6 w-6 text-indigo-400" />}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-white leading-6">
              {worker.name}
            </h2>
            <SectionEditButton onClick={openNameModal} />
          </div>
          <div className="mt-1">
            <Badge color="indigo">
              {WORKER_TYPES_LABELS[worker.type] || worker.type}
            </Badge>
          </div>
        </div>
      </div>
    );

    const { pattern, jsonPath } = worker.config || {};
    const hasValidation = isParser && (pattern || jsonPath);

    return (
      <div>
        <CardHeader
          leftAction={headerContent}
        >
          <div className="px-4 py-5 space-y-6">
            {/* Configuration */}
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium text-white">Configuration</h3>
                {isParser && (
                  <SectionEditButton onClick={() => setEditModal('configuration')} />
                )}
              </div>

              {isParser && worker.config ? (
                <div className="space-y-6">
                  <InfoBlock
                    label={worker.config.method}
                    value={worker.config.url}
                  />

                  <div className="space-y-4">
                    <CodeBlock label="Headers" data={worker.config.headers} />
                    {worker.config.method === WORKER_PARSER_METHODS.POST && (
                      <CodeBlock label="Body" data={worker.config.body} />
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 italic">No configuration available for this worker type.</p>
              )}
            </div>

            {/* Validation Config */}
            {hasValidation && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-medium text-white">Validation</h3>
                  <SectionEditButton onClick={() => setEditModal('validation')} />
                </div>
                <div className="space-y-3">
                  {pattern && (
                    <InfoBlock
                      label={WORKER_PARSER_VALIDATION_TYPES_LABELS[WORKER_PARSER_VALIDATION_TYPES.REGEX]}
                      value={pattern}
                    />
                  )}
                  {jsonPath && (
                    <InfoBlock
                      label={WORKER_PARSER_VALIDATION_TYPES_LABELS[WORKER_PARSER_VALIDATION_TYPES.JSON]}
                      value={jsonPath}
                    />
                  )}
                  {worker.config.operation && worker.config.operation !== WORKER_PATH_OPERATIONS.NONE && (
                    <InfoBlock
                      label="Condition"
                      value={`${WORKER_PATH_OPERATIONS_LABELS[worker.config.operation] || worker.config.operation} ${worker.config.controlValue ?? ''}`}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Actions Config */}
            {isParser && worker.config && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-medium text-white">Actions</h3>
                  <SectionEditButton onClick={() => setEditModal('actions')} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {renderActionSection('On Match', worker.config.onMatch)}
                  {renderActionSection('On No Match', worker.config.onNoMatch)}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
      </div>
    );
  };

  const getValidationInitialValues = () => {
    if (!worker?.config) return {};
    const { pattern, jsonPath, operation, controlValue } = worker.config;
    const base = { operation, controlValue };
    if (pattern) return { ...base, validationType: WORKER_PARSER_VALIDATION_TYPES.REGEX, expression: pattern };
    if (jsonPath) return { ...base, validationType: WORKER_PARSER_VALIDATION_TYPES.JSON, expression: jsonPath };
    return base;
  };

  const getActionsInitialValues = () => {
    if (!worker?.config) return {};
    return {
      onSuccess: worker.config.onMatch?.action,
      onSuccessMessage: worker.config.onMatch?.messageTemplate || '',
      onFailure: worker.config.onNoMatch?.action,
      onFailureMessage: worker.config.onNoMatch?.messageTemplate || ''
    };
  };

  const editModalTitles = {
    name: 'Edit Name',
    configuration: 'Edit Configuration',
    validation: 'Edit Validation',
    actions: 'Edit Actions'
  };

  const renderEditModalContent = () => {
    switch (editModal) {
      case 'name':
        return (
          <div className="space-y-6 p-4">
            <BaseTextInput
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Worker name"
            />
            <div className="flex justify-end gap-3 relative z-20">
              <Button onClick={() => setEditModal(null)}>
                Cancel
              </Button>
              <PrimaryButton onClick={handleNameSave} disabled={!editName.trim()} saving={updating}>
                Save Changes
              </PrimaryButton>
            </div>
          </div>
        );
      case 'configuration':
        return (
          <ParserUrlForm
            initialValues={worker.config}
            onSubmit={handleConfigurationSave}
            onCancel={() => setEditModal(null)}
            saving={updating}
          />
        );
      case 'validation':
        return (
          <ParserValidationForm
            initialValues={getValidationInitialValues()}
            onSubmit={handleValidationSave}
            onCancel={() => setEditModal(null)}
            saving={updating}
          />
        );
      case 'actions':
        return (
          <ValidationLogicForm
            initialValues={getActionsInitialValues()}
            onSubmit={handleActionsSave}
            onCancel={() => setEditModal(null)}
            saving={updating}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Page title="Worker Details" headerActions={<WorkerTabs />}>
      {renderContent()}

      {!isFetching && (
        <div className="mx-auto mt-6 px-4 flex max-w-7xl justify-end">
          <Button onClick={() => navigate(returnTo)}>
            Cancel
          </Button>
        </div>
      )}

      {worker && (
        <FullScreenModal
          isOpen={editModal !== null}
          onClose={() => setEditModal(null)}
          title={editModalTitles[editModal] || ''}
          loading={updating}
        >
          {renderEditModalContent()}
        </FullScreenModal>
      )}
    </Page>
  );
}
